// src/services/pharmacyService.js
import apiService from './api';

class PharmacyService {
  // Profile Management
  async getProfile() {
    try {
      const response = await apiService.pharmacies.getProfile();
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch pharmacy profile',
        data: null
      };
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await apiService.pharmacies.updateProfile(profileData);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Profile updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update pharmacy profile'
      };
    }
  }

  async uploadProfileImage(formData) {
    try {
      const response = await apiService.pharmacies.uploadProfileImage(formData);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Profile image uploaded successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to upload profile image'
      };
    }
  }

  // Public pharmacy listing
  async getAllPharmacies(params = {}) {
    try {
      const response = await apiService.pharmacies.getAll(params);
      return {
        success: true,
        data: {
          pharmacies: response.data?.pharmacies || response.data || [],
          pagination: response.data?.pagination || {
            currentPage: params.page || 1,
            totalPages: 1,
            totalItems: (response.data?.pharmacies || response.data || []).length,
            itemsPerPage: params.limit || 12
          }
        },
        message: response.message || 'Pharmacies fetched successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch pharmacies',
        data: {
          pharmacies: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 12
          }
        }
      };
    }
  }

  async getPharmacyById(id) {
    try {
      const response = await apiService.pharmacies.getById(id);
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch pharmacy',
        data: null
      };
    }
  }

  // Get public products for a specific pharmacy
  async getPharmacyProducts(pharmacyId, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await apiService.get(
        `/pharmacies/${pharmacyId}/products${queryString ? `?${queryString}` : ''}`
      );
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch pharmacy products',
        data: { products: [], pagination: null }
      };
    }
  }

  async getNearbyPharmacies(params = {}) {
    try {
      const response = await apiService.pharmacies.getNearby(params);
      return {
        success: true,
        data: {
          pharmacies: response.data?.pharmacies || response.data || [],
          total: response.data?.total || (response.data?.pharmacies || response.data || []).length,
          pagination: response.data?.pagination
        },
        message: response.message || 'Nearby pharmacies fetched successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch nearby pharmacies',
        data: {
          pharmacies: [],
          total: 0
        }
      };
    }
  }

  async searchPharmacies(query, filters = {}) {
    try {
      const response = await apiService.pharmacies.search(query, filters);
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to search pharmacies',
        data: []
      };
    }
  }

  // Product/Medicine Management (for pharmacy owners)
  async getProducts(params = {}) {
    try {
      const response = await apiService.pharmacies.getProducts(params);
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch products',
        data: []
      };
    }
  }

  async createProduct(productData) {
    try {
      const response = await apiService.pharmacies.createProduct(productData);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Product created successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to create product'
      };
    }
  }

  async updateProduct(id, productData) {
    try {
      const response = await apiService.pharmacies.updateProduct(id, productData);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Product updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update product'
      };
    }
  }

  async deleteProduct(id) {
    try {
      const response = await apiService.pharmacies.deleteProduct(id);
      return {
        success: true,
        message: response.message || 'Product deleted successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to delete product'
      };
    }
  }

  async uploadProductImage(id, imageFile) {
    try {
      const response = await apiService.pharmacies.uploadProductImage(id, imageFile);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Image uploaded successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to upload product image'
      };
    }
  }

  // Order Management
  async getOrders(params = {}) {
    try {
      const response = await apiService.pharmacies.getOrders(params);
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch orders',
        data: []
      };
    }
  }

  async updateOrderStatus(id, statusData) {
    try {
      const response = await apiService.pharmacies.updateOrderStatus(id, statusData);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Order status updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to update order status'
      };
    }
  }

  // Prescription Verification
  async verifyPrescription(orderId, verificationData) {
    try {
      const response = await apiService.put(
        `/pharmacies/orders/${orderId}/verify-prescription`,
        verificationData
      );
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Prescription verified successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to verify prescription'
      };
    }
  }

  // Statistics
  async getStats() {
    try {
      const response = await apiService.pharmacies.getStats();
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch statistics',
        data: null
      };
    }
  }

  // Subscription Management
  async getSubscriptionPlans() {
    try {
      const response = await apiService.pharmacies.getSubscriptionPlans();
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch subscription plans',
        data: []
      };
    }
  }

  async getSubscription() {
    try {
      const response = await apiService.pharmacies.getSubscription();
      return {
        success: true,
        data: response.data || response,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to fetch subscription',
        data: null
      };
    }
  }

  async subscribe(subscriptionData) {
    try {
      const response = await apiService.pharmacies.subscribe(subscriptionData);
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Subscription successful'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Subscription failed'
      };
    }
  }

  async cancelSubscription() {
    try {
      const response = await apiService.pharmacies.cancelSubscription();
      return {
        success: true,
        message: response.message || 'Subscription cancelled successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to cancel subscription'
      };
    }
  }

  // Utility functions
  validateProductData(data) {
    const errors = [];
    
    if (!data.name || data.name.trim().length < 2) {
      errors.push('Product name must be at least 2 characters');
    }
    
    if (!data.description || data.description.trim().length < 10) {
      errors.push('Description must be at least 10 characters');
    }
    
    if (!data.category) {
      errors.push('Category is required');
    }
    
    if (!data.price || data.price <= 0) {
      errors.push('Valid price is required');
    }
    
    if (data.stock === undefined || data.stock < 0) {
      errors.push('Valid stock quantity is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  formatCurrency(amount, currency = 'KES') {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  getStockStatus(stock) {
    if (stock === 0) return { label: 'Out of Stock', color: 'red' };
    if (stock < 10) return { label: 'Low Stock', color: 'orange' };
    return { label: 'In Stock', color: 'green' };
  }

  getOrderStatusColor(status) {
    const statusColors = {
      pending: 'yellow',
      confirmed: 'blue',
      preparing: 'purple',
      ready: 'cyan',
      out_for_delivery: 'indigo',
      delivered: 'green',
      cancelled: 'red',
      returned: 'gray'
    };
    return statusColors[status] || 'gray';
  }

  getOrderStatusLabel(status) {
    const statusLabels = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      preparing: 'Preparing',
      ready: 'Ready for Pickup',
      out_for_delivery: 'Out for Delivery',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      returned: 'Returned'
    };
    return statusLabels[status] || status;
  }
}

const pharmacyService = new PharmacyService();

export { pharmacyService };
export default pharmacyService;